import { useEffect, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DatePicker from "@/components/DatePicker";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    BadgeIndianRupee,
    FileDown,
    Sparkles,
    Loader2,
    AlertCircle,
    FileSpreadsheet,
    ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

function startOfMonthIso() {
    const d = new Date();
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))
        .toISOString()
        .slice(0, 10);
}
function todayIso() {
    return new Date().toISOString().slice(0, 10);
}

export default function Billing() {
    const [clients, setClients] = useState([]);
    const [client, setClient] = useState("all");
    const [from, setFrom] = useState(startOfMonthIso());
    const [to, setTo] = useState(todayIso());
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
        load(); // initial
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const params = { from_date: from, to_date: to };
            if (client !== "all") params.client_name = client;
            const { data } = await api.get("/billing/summary", { params });
            setSummary(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const generatePdf = () => {
        const token = localStorage.getItem("sdc_token");
        const params = new URLSearchParams({
            from_date: from,
            to_date: to,
            auth: token || "",
        });
        if (client !== "all") params.set("client_name", client);
        window.open(`${API}/billing/pdf?${params.toString()}`, "_blank");
    };

    const exportData = (format) => {
        const token = localStorage.getItem("sdc_token");
        const params = new URLSearchParams({
            from_date: from,
            to_date: to,
            format,
            auth: token || "",
        });
        if (client !== "all") params.set("client_name", client);
        window.location.href = `${API}/exports/billing?${params.toString()}`;
    };

    const totalReimb = summary?.total_reimbursable || 0;
    const billed = summary?.billed_count || 0;
    const pending = summary?.pending_count || 0;
    const tasks = summary?.tasks || [];

    return (
        <div className="space-y-6" data-testid="billing-page">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                        Revenue
                    </div>
                    <h1 className="font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
                        Billing summary
                    </h1>
                    <p className="mt-2 text-sm text-muted-foreground">
                        Pick a client and date range — SDC compiles every completed engagement
                        and reimbursable expense, ready to invoice.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" data-testid="billing-export-trigger">
                                <FileSpreadsheet className="mr-1.5 h-4 w-4" />
                                Export
                                <ChevronDown className="ml-1 h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem
                                onClick={() => exportData("xlsx")}
                                data-testid="billing-export-xlsx"
                            >
                                Excel (.xlsx)
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() => exportData("csv")}
                                data-testid="billing-export-csv"
                            >
                                CSV (.csv)
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    <Button onClick={generatePdf} data-testid="billing-pdf-button">
                        <FileDown className="mr-1.5 h-4 w-4" />
                        Download PDF
                    </Button>
                </div>
            </div>

            <Card className="sdc-card p-4">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
                    <div className="md:col-span-4">
                        <Label className="mb-1.5 block">Client</Label>
                        <Select value={client} onValueChange={setClient}>
                            <SelectTrigger data-testid="billing-client-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All clients</SelectItem>
                                {clients.map((c) => (
                                    <SelectItem key={c.id} value={c.name}>
                                        {c.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="md:col-span-3">
                        <Label className="mb-1.5 block">From</Label>
                        <DatePicker
                            value={from}
                            onChange={setFrom}
                            placeholder="Start date"
                            testId="billing-from"
                        />
                    </div>
                    <div className="md:col-span-3">
                        <Label className="mb-1.5 block">To</Label>
                        <DatePicker
                            value={to}
                            onChange={setTo}
                            placeholder="End date"
                            testId="billing-to"
                        />
                    </div>
                    <div className="md:col-span-2 flex items-end">
                        <Button
                            className="w-full"
                            onClick={load}
                            disabled={loading}
                            data-testid="billing-apply"
                        >
                            {loading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                "Apply"
                            )}
                        </Button>
                    </div>
                </div>
            </Card>

            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <Card className="sdc-card p-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                                Completed
                            </div>
                            <div className="mt-2 font-display text-3xl font-extrabold tracking-tight">
                                {tasks.length}
                            </div>
                        </div>
                        <Sparkles className="h-5 w-5 text-primary" />
                    </div>
                </Card>
                <Card className="sdc-card p-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                                Already billed
                            </div>
                            <div className="mt-2 font-display text-3xl font-extrabold tracking-tight text-emerald-700">
                                {billed}
                            </div>
                        </div>
                        <BadgeIndianRupee className="h-5 w-5 text-emerald-700" />
                    </div>
                </Card>
                <Card className="sdc-card p-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                                Pending to bill
                            </div>
                            <div className="mt-2 font-display text-3xl font-extrabold tracking-tight text-amber-700">
                                {pending}
                            </div>
                        </div>
                        <AlertCircle className="h-5 w-5 text-amber-700" />
                    </div>
                </Card>
                <Card className="sdc-card p-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                                Reimbursable
                            </div>
                            <div className="mt-2 font-display text-3xl font-extrabold tracking-tight text-primary">
                                ₹{totalReimb.toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                            </div>
                        </div>
                        <BadgeIndianRupee className="h-5 w-5 text-primary" />
                    </div>
                </Card>
            </div>

            <Card className="sdc-card overflow-hidden">
                <div className="border-b border-border px-5 py-3">
                    <h2 className="font-display text-lg font-extrabold tracking-tight">
                        Completed engagements in range
                    </h2>
                </div>
                {tasks.length === 0 ? (
                    <div className="py-14 text-center text-sm text-muted-foreground">
                        No completed engagements match the filters.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-secondary/40 hover:bg-secondary/40">
                                    <TableHead>Date</TableHead>
                                    <TableHead>Client</TableHead>
                                    <TableHead>Work</TableHead>
                                    <TableHead className="text-center">Billed</TableHead>
                                    <TableHead className="text-center">Reimb.</TableHead>
                                    <TableHead className="text-right pr-6">Amount</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {tasks.map((t) => (
                                    <TableRow key={t.id} className="sdc-table-row">
                                        <TableCell className="text-sm">{t.task_date}</TableCell>
                                        <TableCell className="font-semibold">
                                            {t.client_name}
                                        </TableCell>
                                        <TableCell>{t.work_type}</TableCell>
                                        <TableCell className="text-center">
                                            {t.billing_status ? (
                                                <span className="sdc-pill border-emerald-200 bg-emerald-50 text-emerald-700">
                                                    Yes
                                                </span>
                                            ) : (
                                                <span className="sdc-pill border-amber-200 bg-amber-50 text-amber-700">
                                                    No
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-center">
                                            {t.reimbursable_expense ? (
                                                <span className="sdc-pill border-primary/20 bg-primary/5 text-primary">
                                                    Yes
                                                </span>
                                            ) : (
                                                "—"
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right pr-6 font-mono text-sm">
                                            {t.reimbursable_expense && t.reimbursable_amount
                                                ? `₹${Number(t.reimbursable_amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
                                                : "—"}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </Card>
        </div>
    );
}
